import {
  type RBACPermissionsContextType,
  useRBACPermissions,
} from '@/contexts/useRBACPermissions'
import { Alert, Button, VStack } from '@chakra-ui/react'
import { useNavigate } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

type AuthorizedRouteProps = {
  children: ReactNode
  fallback?: ReactNode
  /**
   * Optional custom permission check. Defaults to `hasAdminRights()`.
   * Use this to gate by a role other than admin (e.g. GTM).
   *
   * @example
   * <AuthorizedRoute permissionCheck={(p) => p.hasGtmRights()}>
   *   <SomePage />
   * </AuthorizedRoute>
   */
  permissionCheck?: (permissions: RBACPermissionsContextType) => boolean
}

/**
 * Component that wraps content and only shows it if the user passes the
 * permission check. By default it requires admin rights, but a custom
 * `permissionCheck` can be provided (e.g. GTM role).
 * Shows an error message if the user does not have permission.
 */
export function AuthorizedRoute({
  children,
  fallback,
  permissionCheck,
}: Readonly<AuthorizedRouteProps>) {
  const permissions = useRBACPermissions()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const hasPermission = permissionCheck
    ? permissionCheck(permissions)
    : permissions.hasAdminRights()

  if (hasPermission) {
    return <>{children}</>
  }

  if (fallback) {
    return <>{fallback}</>
  }

  // Default unauthorized message
  return (
    <VStack gap={6} align="center" justify="center" minH="400px" p={6}>
      <Alert.Root status="error" maxW="500px">
        <Alert.Indicator />
        <VStack align="start" gap={2}>
          <Alert.Title>{t('authorization.accessDenied')}</Alert.Title>
          <Alert.Description>
            {t('authorization.insufficientPermissions')}
          </Alert.Description>
        </VStack>
      </Alert.Root>
      <Button variant="outline" onClick={() => navigate({ to: '/' })}>
        {t('authorization.goToHome')}
      </Button>
    </VStack>
  )
}
